import {
  ComponentConfig,
  ComponentType,
} from "../services/types/ComponentTypes";
import { walkComponentTree } from "../state/utils/configUtils";

const parseFormAmount = (value: unknown): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = parseFloat(String(value).replace(/,/g, "").trim());
  return Number.isFinite(parsed) ? parsed : null;
};

/** Read a numeric form value when the key is present (0 is valid). */
const getFormAmount = (
  data: Record<string, unknown>,
  key: string
): number | undefined => {
  if (!(key in data)) return undefined;
  const parsed = parseFormAmount(data[key]);
  return parsed === null ? undefined : parsed;
};

type TotalAmountField = {
  bindValue?: string;
  jsonAttribute?: string;
};

const isTotalAmountField = (comp: Record<string, unknown>): boolean => {
  const bindValue = String(comp.BindValue || comp.name || "").trim();
  const jsonAttribute = String(
    comp.Json_Attribute || comp.json_Attribute || ""
  ).trim();
  const label = String(comp.Label || comp.label || "").trim();

  return (
    bindValue === "txt_total_amount" ||
    jsonAttribute === "amount" ||
    jsonAttribute === "est_total_amount" ||
    jsonAttribute === "total_amount" ||
    /^total amount$/i.test(label)
  );
};

const findTotalAmountField = (
  config: ComponentConfig[]
): TotalAmountField | null => {
  let found: TotalAmountField | null = null;

  walkComponentTree(config, (component) => {
    if (found) return;
    const comp = component as Record<string, unknown>;
    if (comp.componentType !== ComponentType.TEXT_FIELD) return;
    if (!isTotalAmountField(comp)) return;

    found = {
      bindValue: String(comp.BindValue || comp.name || "").trim() || undefined,
      jsonAttribute:
        String(comp.Json_Attribute || comp.json_Attribute || "").trim() ||
        undefined,
    };
  });

  return found;
};

/** Prefer BindValue (ExpressionV2 live total) over stale Json_Attribute from edit load. */
export const resolveCurrentTotalAmount = (
  formData: Record<string, unknown>,
  totalField: TotalAmountField | null = null
): number => {
  const bindValue = totalField?.bindValue ?? "txt_total_amount";
  const jsonAttribute = totalField?.jsonAttribute;

  const fromBind = getFormAmount(formData, bindValue);
  if (fromBind !== undefined) return fromBind;

  if (jsonAttribute && jsonAttribute !== bindValue) {
    const fromJson = getFormAmount(formData, jsonAttribute);
    if (fromJson !== undefined) return fromJson;
  }

  for (const fallback of ["txt_total_amount", "amount", "est_total_amount"]) {
    const value = getFormAmount(formData, fallback);
    if (value !== undefined) return value;
  }

  return 0;
};

const findLineNetAmountKeys = (
  subModule: Record<string, unknown>
): { bind: string; json: string } => {
  for (const control of (subModule.childControls as Record<string, unknown>[]) ||
    []) {
    const bind = String(control.BindValue || control.name || "").trim();
    const json = String(
      control.Json_Attribute || control.json_Attribute || ""
    ).trim();
    if (bind === "txt_net_amt" || json === "net_amt") {
      return {
        bind: bind || "txt_net_amt",
        json: json || "net_amt",
      };
    }
  }

  return { bind: "txt_net_amt", json: "net_amt" };
};

/**
 * Resolve submodule rows for zero-amount checks.
 * Prefer live BindValue (what SubModule publishes while editing) over Name/TableName
 * left from edit load. Do not merge with Name — merge can let stale Name fields
 * overwrite live BindValue values (e.g. zeroed rate/qty).
 */
const getSubModuleRows = (
  formData: Record<string, unknown>,
  subModule: Record<string, unknown>
): Record<string, unknown>[] => {
  const bindValue =
    String(subModule.BindValue || subModule.bindValue || "").trim() || undefined;
  const nameAliases = [
    String(subModule.name || "").trim(),
    String(subModule.Name || "").trim(),
    String(subModule.TableName || "").trim(),
    String(subModule.tableName || "").trim(),
  ].filter(Boolean);

  const pickNonEmpty = (key: string | undefined): Record<string, unknown>[] | null => {
    if (!key) return null;
    const rows = formData[key];
    if (Array.isArray(rows) && rows.length > 0) {
      return rows as Record<string, unknown>[];
    }
    return null;
  };

  const fromBind = pickNonEmpty(bindValue);
  if (fromBind) return fromBind;

  for (const key of [...new Set(nameAliases)]) {
    if (bindValue && key === bindValue) continue;
    const rows = pickNonEmpty(key);
    if (rows) return rows;
  }

  // Empty arrays still count as "present" for callers that check length
  if (bindValue && Array.isArray(formData[bindValue])) {
    return formData[bindValue] as Record<string, unknown>[];
  }
  for (const key of [...new Set(nameAliases)]) {
    if (Array.isArray(formData[key])) {
      return formData[key] as Record<string, unknown>[];
    }
  }

  return [];
};

const resolveRowNetAmount = (
  row: Record<string, unknown>,
  keys: { bind: string; json: string }
): number => {
  const fromBind = getFormAmount(row, keys.bind);
  if (fromBind !== undefined) return fromBind;

  const fromJson = getFormAmount(row, keys.json);
  if (fromJson !== undefined) return fromJson;

  return 0;
};

/** Keys that indicate a submodule row was intentionally filled in (not a blank template row). */
const POPULATED_ROW_FIELD_KEYS = [
  "drp_code",
  "codeid",
  "code_id",
  "item_id",
  "product_id",
  "txt_narration",
  "narration",
  "drp_department",
  "department_id",
  "drp_item",
  "itemcode",
  "id",
];

const hasNonEmptyValue = (value: unknown): boolean =>
  value !== undefined && value !== null && String(value).trim() !== "";

/** True when the row has item/department/narration data — not an untouched new row. */
export const isPopulatedSubModuleRow = (
  row: Record<string, unknown>
): boolean => {
  for (const key of POPULATED_ROW_FIELD_KEYS) {
    if (hasNonEmptyValue(row[key])) return true;
  }
  return false;
};

export const hasZeroNetPopulatedLineItem = (
  config: ComponentConfig[],
  formData: Record<string, unknown>
): boolean => {
  let found = false;

  walkComponentTree(config, (component) => {
    if (found) return;
    const subModule = component as Record<string, unknown>;
    if (subModule.componentType !== ComponentType.SUB_MODULE) return;

    const rows = getSubModuleRows(formData, subModule);
    if (rows.length === 0) return;

    const netKeys = findLineNetAmountKeys(subModule);
    for (const row of rows) {
      if (
        isPopulatedSubModuleRow(row) &&
        resolveRowNetAmount(row, netKeys) === 0
      ) {
        found = true;
        return;
      }
    }
  });

  return found;
};

export const sumLineItemNetAmounts = (
  config: ComponentConfig[],
  formData: Record<string, unknown>
): { sum: number; hasLineItems: boolean } => {
  let sum = 0;
  let hasLineItems = false;

  walkComponentTree(config, (component) => {
    const subModule = component as Record<string, unknown>;
    if (subModule.componentType !== ComponentType.SUB_MODULE) return;

    const rows = getSubModuleRows(formData, subModule);
    if (rows.length === 0) return;

    hasLineItems = true;
    const netKeys = findLineNetAmountKeys(subModule);
    for (const row of rows) {
      sum += resolveRowNetAmount(row, netKeys);
    }
  });

  return { sum, hasLineItems };
};

/**
 * Returns true when save should show the zero-amount confirmation dialog.
 * Uses live calculated totals (BindValue) and line-item net amounts, not stale
 * Json_Attribute values loaded during edit.
 */
export const shouldConfirmZeroAmountSubmission = (
  config: ComponentConfig[],
  formData: Record<string, unknown>
): boolean => {
  const totalField = findTotalAmountField(config);
  const currentTotal = resolveCurrentTotalAmount(formData, totalField);
  const { sum: lineNetSum, hasLineItems } = sumLineItemNetAmounts(
    config,
    formData
  );

  if (currentTotal === 0) return true;
  if (hasLineItems && lineNetSum === 0) return true;
  if (hasZeroNetPopulatedLineItem(config, formData)) return true;

  return false;
};
